import { inject, Injectable } from '@angular/core';
import { AbsenceStatus } from '@reducers/orm/absence/absence.model';
import { ExchangeStatusType } from '@reducers/orm/exchange/exchange.model';
import { reformatListResponse } from '@reducers/shared/entity.helper';
import { ApiGateway, ArrayStrategy, objectToSearchParams } from '@sb/shared/api';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addWeeks, addYears, subDays, subYears } from 'date-fns';
import { map } from 'rxjs/operators';

import { FIRST_PAGE } from '../request-paging';
import type {
  PageRequest,
  PendingAbsenceRow,
  PendingExchangeRow,
  PendingOpenShiftRow,
  PendingRequestsPage,
  PendingRow,
} from './requests.models';

const readRows = (body: unknown): Record<string, unknown>[] => {
  if (Array.isArray(body)) {
    return body;
  }

  if (body !== null && typeof body === 'object' && 'data' in body && Array.isArray(body.data)) {
    return body.data;
  }

  return [];
};

const readPagination = (body: unknown): { page: number; pages: number } => {
  const singlePage = { page: FIRST_PAGE, pages: FIRST_PAGE };

  if (body === null || typeof body !== 'object' || !('meta' in body)) {
    return singlePage;
  }

  const meta = body.meta;

  if (meta === null || typeof meta !== 'object' || !('page' in meta) || !('pages' in meta)) {
    return singlePage;
  }

  const page = Number.parseInt(String(meta.page), 10);
  const pages = Number.parseInt(String(meta.pages), 10);

  return Number.isFinite(page) && Number.isFinite(pages) ? { page, pages } : singlePage;
};

const toPage =
  <TRow extends PendingRow>(entityKey: string) =>
  (body: unknown): PendingRequestsPage<TRow> => {
    const rows = reformatListResponse<TRow>(entityKey, readRows(body));
    const { page, pages } = readPagination(body);

    return { rows, page, pages, hasMore: page < pages };
  };

const pageParams = (page?: PageRequest): Record<string, number> => (page ? { limit: page.limit, page: page.page } : {});

const absencePeriodParams = () => ({
  minDate: toISODateString(subYears(new Date(), 1)),
  maxDate: toISODateString(addYears(new Date(), 2)),
});

const ownAbsencePeriodParams = () => ({
  minDate: toISODateString(subYears(new Date(), 1)),
  maxDate: toISODateString(subDays(addYears(new Date(), 5), 1)),
});

// Both bounds default to today independently when omitted (IndexRequest::dateOrToday in core),
// so without these the endpoint only ever returns today's open shifts.
const openShiftPeriodParams = () => ({
  minDate: toISODateString(new Date()),
  maxDate: toISODateString(addWeeks(new Date(), 26)),
});

const PENDING_EXCHANGE_STATUSES = [ExchangeStatusType.PENDING_EMPLOYEE, ExchangeStatusType.PENDING_SUPERVISOR];

@Injectable({ providedIn: 'root' })
export class PendingRequestsApiService {
  #gateway = inject(ApiGateway);

  listPendingExchanges(page?: PageRequest) {
    return this.#listExchanges({ status: PENDING_EXCHANGE_STATUSES, ...pageParams(page) });
  }

  listPendingAbsences(page?: PageRequest) {
    return this.#listAbsences({
      ...absencePeriodParams(),
      status: AbsenceStatus.PENDING,
      include: 'AbsenteeDay',
      ...pageParams(page),
    });
  }

  listApprovedOpenEndedAbsences(page?: PageRequest) {
    return this.#listAbsences({
      ...absencePeriodParams(),
      status: AbsenceStatus.APPROVED,
      onlyOpenEnded: true,
      include: 'AbsenteeDay',
      ...pageParams(page),
    });
  }

  listIncomingExchanges(userId: string, page?: PageRequest) {
    return this.#listExchanges({ incomingUser: userId, status: PENDING_EXCHANGE_STATUSES, ...pageParams(page) });
  }

  listOutgoingExchanges(userId: string, page?: PageRequest) {
    return this.#listExchanges({ requestedBy: userId, status: PENDING_EXCHANGE_STATUSES, ...pageParams(page) });
  }

  listOpenShiftsAwaitingReaction(page?: PageRequest) {
    return this.#list<PendingOpenShiftRow>(
      'open_shifts',
      objectToSearchParams({ awaitingReaction: 'me', ...openShiftPeriodParams(), ...pageParams(page) }),
      'OpenShift',
    );
  }

  listOwnPendingAbsences(userId: string, page?: PageRequest) {
    return this.#listAbsences({
      userId,
      ...ownAbsencePeriodParams(),
      status: AbsenceStatus.PENDING,
      include: 'AbsenteeDay',
      ...pageParams(page),
    });
  }

  #listExchanges(request: object) {
    return this.#list<PendingExchangeRow>(
      'exchanges',
      objectToSearchParams(request, ArrayStrategy.MULTIPLE_PARAMS),
      'Exchange',
    );
  }

  #listAbsences(request: object) {
    return this.#list<PendingAbsenceRow>('absentees', objectToSearchParams(request), 'Absentee');
  }

  #list<TRow extends PendingRow>(endpoint: string, params: ReturnType<typeof objectToSearchParams>, entityKey: string) {
    return this.#gateway.get<unknown>(endpoint, { params, meta: true }).pipe(map(toPage<TRow>(entityKey)));
  }
}
